import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getAllIngredients, getCategories, getCategoryInfo } from '../data/ingredientsData';

function IngredientsLibrary() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const allIngredients = getAllIngredients();
  const categories = getCategories();
  const categoryInfo = getCategoryInfo();

  const filteredIngredients = selectedCategory === 'all'
    ? allIngredients
    : allIngredients.filter(ing => ing.categorySlug === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Ingredients Research Library | FoodLens AI - Scientific Analysis of Food Ingredients</title>
        <meta name="description" content="Comprehensive research library on food ingredients including seed oils, sugars, and artificial sweeteners. Each ingredient includes scientific studies, health concerns, and expert analysis." />
        <meta name="keywords" content="food ingredients research, seed oils health effects, artificial sweeteners studies, sugar research, nutrition science, food safety, ingredient analysis" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Ingredients Research Library | FoodLens AI" />
        <meta property="og:description" content="Research-backed analysis of food ingredients with scientific citations and expert insights." />
        <meta property="og:url" content="https://foodlens-ai.com/ingredients" />
        <link rel="canonical" href="https://foodlens-ai.com/ingredients" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-hero py-16 md:py-24">
        <div className="section text-center">
          <h1 className="section-heading">Ingredients Research Library</h1>
          <p className="section-subheading mx-auto">
            Science-backed information about common food ingredients. Each entry includes research studies, health concerns, and peer-reviewed citations.
          </p>
        </div>
      </section>

      <div className="section py-12">
        {/* Category Filter */}
        <div className="mb-10">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-sage-500 text-white shadow-soft'
                  : 'bg-surface text-gray-500 hover:bg-sage-50 hover:text-sage-600 border border-sage-100'
              }`}
            >
              All Ingredients
            </button>
            {Object.entries(categoryInfo).map(([slug, info]) => (
              <button
                key={slug}
                onClick={() => setSelectedCategory(slug)}
                className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                  selectedCategory === slug
                    ? 'bg-sage-500 text-white shadow-soft'
                    : 'bg-surface text-gray-500 hover:bg-sage-50 hover:text-sage-600 border border-sage-100'
                }`}
              >
                {info.icon} {info.name}
              </button>
            ))}
          </div>
        </div>

        {/* Category Description */}
        {selectedCategory !== 'all' && (
          <div className="mb-10 p-6 bg-sage-100 rounded-2xl">
            <h2 className="font-display text-2xl text-charcoal mb-2">
              {categoryInfo[selectedCategory].icon} {categoryInfo[selectedCategory].name}
            </h2>
            <p className="text-gray-500">
              {categoryInfo[selectedCategory].description}
            </p>
          </div>
        )}

        {/* Ingredients Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredIngredients.map((ingredient, index) => (
            <Link
              key={ingredient.id}
              to={`/ingredients/${ingredient.id}`}
              className="bg-surface border border-sage-100 rounded-2xl p-6 hover:shadow-elevated hover:-translate-y-1 transition-all duration-300 animate-fade-in-up"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-display text-xl text-charcoal">
                  {ingredient.name}
                </h3>
                <span className="text-2xl">
                  {categoryInfo[ingredient.categorySlug].icon}
                </span>
              </div>

              <div className="mb-3">
                <span className="inline-block bg-sage-100 text-sage-700 px-3 py-1 rounded-lg text-xs font-medium">
                  {ingredient.category}
                </span>
              </div>

              <p className="text-gray-500 text-sm mb-4 line-clamp-3 leading-relaxed">
                {ingredient.description}
              </p>

              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>
                  {ingredient.concerns.length} concern{ingredient.concerns.length !== 1 ? 's' : ''}
                </span>
                <span>
                  {ingredient.references.length} paper{ingredient.references.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-center text-sage-600 font-medium text-sm">
                View Research
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-sage rounded-3xl p-8 md:p-12 text-center text-white">
          <h2 className="font-display text-3xl mb-4">
            Detect These Ingredients Instantly
          </h2>
          <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
            FoodLens AI scans food labels and instantly identifies harmful ingredients like seed oils, artificial sweeteners, and additives.
          </p>
          <Link
            to="/app"
            className="inline-block px-8 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
          >
            Download FoodLens AI
          </Link>
        </div>
      </div>
    </div>
  );
}

export default IngredientsLibrary;
